import {
  useAdminCheckpoints,
  useCreateCheckpoint,
  useStampCheckpointAnchor,
  useUpgradeCheckpointAnchor,
} from '../../hooks/useAdmin'

const OTS_STATUS_LABELS: Record<string, string> = {
  none: 'Not anchored',
  pending: 'Anchoring — awaiting confirmation',
  confirmed: 'Anchored to Bitcoin',
}

export default function AdminCheckpointsPage() {
  const { data, isLoading, isError } = useAdminCheckpoints()
  const createMutation = useCreateCheckpoint()
  const stampMutation = useStampCheckpointAnchor()
  const upgradeMutation = useUpgradeCheckpointAnchor()

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Chain-tip checkpoints</h1>
          <p className="text-sm text-gray-500 mt-1">
            A daily cron creates and stamps a checkpoint automatically. These actions are for
            on-demand/manual use — see /ledger/verify for the public independent-verification view.
          </p>
        </div>
        <button
          type="button"
          disabled={createMutation.isPending}
          onClick={() => createMutation.mutate()}
          className="shrink-0 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold px-3 py-1.5 transition"
        >
          {createMutation.isPending ? 'Creating…' : 'Create checkpoint'}
        </button>
      </div>

      {isLoading && <p className="text-sm text-gray-500 mt-4">Loading checkpoints…</p>}
      {isError && <p className="text-sm text-red-600 mt-4">Failed to load checkpoints.</p>}

      <div className="mt-4 space-y-3">
        {data?.map((c) => (
          <div key={c.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm font-mono">
                <p className="text-gray-900 font-semibold">#{c.chain_sequence} · {c.entry_count} entries</p>
                <p className="text-gray-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</p>
                <p className="text-gray-600 mt-0.5">
                  {OTS_STATUS_LABELS[c.ots_status] ?? c.ots_status}
                  {c.ots_bitcoin_block_height != null && ` (block ${c.ots_bitcoin_block_height.toLocaleString()})`}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                {c.ots_status === 'none' && (
                  <button
                    type="button"
                    disabled={stampMutation.isPending}
                    onClick={() => stampMutation.mutate(c.id)}
                    className="rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-semibold px-3 py-1.5 transition"
                  >
                    Stamp anchor
                  </button>
                )}
                {c.ots_status === 'pending' && (
                  <button
                    type="button"
                    disabled={upgradeMutation.isPending}
                    onClick={() => upgradeMutation.mutate(c.id)}
                    className="rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-semibold px-3 py-1.5 transition"
                  >
                    Check confirmation
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {data && data.length === 0 && (
          <p className="text-sm text-gray-500">No checkpoints have been created yet.</p>
        )}
      </div>
    </div>
  )
}
